import Select from "react-select";
import SelectWithAdd from "../SelectWithAdd";
import Button from "../Button";
import { MdDeleteForever } from "react-icons/md";

const InputField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  unit,
  required,
  error,
  options = [],
  onAddNewClick,
  disabled = false,
  readOnly = false,
  accept = "",
  icon: Icon = null,
  iconPosition = "left",
  iconBg = "",
  iconColor = "text-gray-400",
  iconOffset = "",
  iconPadding = "",
  gridCols = "",
  bgColor = "",
  currentFileName = null,
  dieId = null,
  onFileRemove = null,
  previewUrl = "",
  checked,
  className,
}) => {
  const defaultPadding = iconPadding
    ? iconPadding
    : iconPosition === "right"
      ? "pl-3 pr-10"
      : "pl-3 pr-3";

  const commonInputClass = `w-full ${defaultPadding} py-2 border rounded-md shadow-sm text-sm focus:outline-none ${error ? "border-red-500" : "border-slate-300"
    } ${readOnly ? "cursor-not-allowed" : ""} ${bgColor}`;

  const renderFileInput = () => (
    <div className="flex flex-col gap-2">
      <div
        className={`border-2 border-dashed rounded-md p-4 flex items-center justify-center relative text-center cursor-pointer transition-colors duration-200 ${disabled
          ? "bg-gray-100 text-gray-400"
          : "bg-white hover:border-primary"
          } ${error ? "border-red-500" : "border-slate-300"}`}
      >
        <label className="w-full h-full flex flex-col items-center justify-center space-y-2 cursor-pointer">
          {type === "image" && previewUrl ? (
            <img
              // src={previewUrl}
              src={
                previewUrl instanceof File
                  ? URL.createObjectURL(previewUrl)
                  : previewUrl
              }
              alt="Preview"
              className="max-h-40 object-contain rounded-md"
            />
          ) : (
            <>
              <span className="text-sm text-slate-600">
                {type === "pdf"
                  ? "Click to upload a PDF file"
                  : "Click to upload an image"}
              </span>
              <span className="text-xs text-slate-400">
                {type === "pdf"
                  ? "Only .pdf allowed"
                  : "Only .jpg/.png/.webp allowed"}
              </span>
            </>
          )}
          <input
            type="file"
            accept={type === "pdf" ? "application/pdf" : "image/*"}
            onChange={(e) => onChange(e.target.files?.[0])}
            disabled={disabled}
            className="hidden"
          />
        </label>
      </div>

      {currentFileName && (
        <div className="flex justify-between items-center text-sm text-slate-600 mt-1">
          <span className="truncate">{currentFileName}</span>
          {dieId && onFileRemove && (
            <Button
              type="button"
              className="text-red-500 hover:text-red-700 text-xs font-medium"
              onClick={() => onFileRemove(dieId)}
              icon={MdDeleteForever}
              iconSize="w-5 h-5"
            />
          )}
        </div>
      )}

      {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
    </div>
  );

  return (
    <div className={`flex flex-col relative ${gridCols}`}>
      {label && (
        <label className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1">
          {label}
          {unit && <span className="text-xs text-slate-500"> ({unit})</span>}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {type === "select" ? (
        <Select
          options={options}
          label={label}
          value={options.find((opt) => opt.value === value) || null}
          onChange={(selected) => onChange(selected?.value || "")}
          // placeholder={`Select ${label}`}
          placeholder={`Select`}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
      ) : type === "selectWithAdd" ? (
        <SelectWithAdd
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          error={error}
          required={required}
          onAddNewClick={onAddNewClick}
        />
      ) : type === "textarea" ? (
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          className={`resize-none ${commonInputClass} ${className || ""}`}
          rows={3}
        />
      ) : type === "checkbox" ? (
        options.length > 0 ? (
          <div className="flex flex-col gap-2">
            {options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={value?.includes(opt.value)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    let newValue = [...(value || [])];
                    if (checked) {
                      newValue.push(opt.value);
                    } else {
                      newValue = newValue.filter((v) => v !== opt.value);
                    }
                    onChange(newValue);
                  }}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={checked ?? value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
            <span className="text-sm text-slate-700">
              {placeholder || label}
            </span>
          </div>
        )
      ) : type === "radio" ? (
        <div className="flex gap-4">
          {options.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2">
              <input
                type="radio"
                name={label}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      ) : type === "pdf" || type === "image" ? (
        renderFileInput()
      ) : (
        <div className="relative">
          {Icon && (
            <span
              className={`absolute ${iconOffset ||
                (iconPosition === "right"
                  ? "right-3 top-2.5"
                  : "left-3 top-2.5")
                } ${iconColor} ${iconBg} p-1 rounded`}
            >
              <Icon size={16} />
            </span>
          )}
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            className={commonInputClass}
          />
        </div>
      )}

      {error && type !== "pdf" && type !== "image" && (
        <span className="text-xs text-red-500 mt-1">{error}</span>
      )}
    </div>
  );
};

export default InputField;
